/* Bend Creator v15 shell. Tokens and sizes follow the v15 prototype and the Figma frame
   "Desktop - 1" (Bend-Creator node 23:9580). They are scoped to the shell so nothing leaks into the app.
   The 3D render keeps its own look: nothing here styles the canvas. */
.shell {
  --bcs-shop-primary: #007da3;
  --bcs-gray-1: #f1f3f5;
  --bcs-gray-2: #e9ecef;
  --bcs-gray-4: #ced4da;
  --bcs-gray-6: #868e96;
  --bcs-gray-7: #495057;
  --bcs-gray-9: #212529;
  --bcs-hairline: rgba(0, 0, 0, 0.15);
  --bcs-border-control: var(--bcs-gray-4);
  --bcs-control-hover: #f6f7f8;
  --bcs-t87: rgba(0, 0, 0, 0.87);
  --bcs-t50: rgba(0, 0, 0, 0.5);
  --bcs-sans: 'IBM Plex Sans', sans-serif;
  --bcs-sans-condensed: 'IBM Plex Sans Condensed', 'IBM Plex Sans', sans-serif;
  --bcs-mono: 'IBM Plex Mono', monospace;
  --bcs-panel-shadow: 0 10px 40px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --bcs-nav-width: 375px;
  --bcs-cart-width: 300px;
  --bcs-gutter: 20px;
  --bcs-top-bar-height: 40px;
  /* Values in the shell's inputs use Plex Mono, as in the prototype. Scoped here so the app keeps its monospace. */
  --mantine-font-family-monospace: var(--bcs-mono);

  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  font-family: var(--bcs-sans);
  color: var(--bcs-gray-9);
  -webkit-font-smoothing: antialiased;
  /* Fast taps on steppers must not trigger Safari's double-tap zoom. */
  touch-action: manipulation;

  /* Inputs: an 8px gap between a label (or its caption) and the control, as in the prototype. */
  :global(.mantine-InputWrapper-label) {
    margin-bottom: 8px;
  }

  :global(.mantine-InputWrapper-label):has(+ :global(.mantine-InputWrapper-description)) {
    margin-bottom: 2px;
  }

  :global(.mantine-InputWrapper-description) {
    margin-bottom: 8px;
  }

  /* Switches: the prototype's 44 × 26 track with a plain white thumb. The size variables are set on the track,
     because Mantine sets its own on the root element's style. */
  :global(.mantine-Switch-track) {
    --switch-width: 44px;
    --switch-height: 26px;
    --switch-thumb-size: 20px;
    --switch-track-label-padding: 3px;
    --switch-color: var(--bcs-shop-primary);
    border: 0;
    background-color: var(--bcs-gray-4);
  }

  :global(.mantine-Switch-input):checked + :global(.mantine-Switch-track) {
    background-color: var(--bcs-shop-primary);
  }

  :global(.mantine-Switch-thumb) {
    border: 0;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  /* No indicator dot inside the thumb. */
  :global(.mantine-Switch-thumb)::before {
    display: none;
  }
}

.canvasLayer {
  position: absolute;
  inset: 0;
  display: flex;
}

.navPanel {
  position: absolute;
  top: var(--bcs-gutter);
  left: var(--bcs-gutter);
  width: var(--bcs-nav-width);
  max-height: calc(100% - 2 * var(--bcs-gutter));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--bcs-hairline);
  border-radius: 10px;
  box-shadow: var(--bcs-panel-shadow);
}

/* Positions the bend list, which draws its own panel or pill. */
.cartSlot {
  position: absolute;
  top: var(--bcs-gutter);
  right: var(--bcs-gutter);
  width: var(--bcs-cart-width);
  max-height: calc(100% - 2 * var(--bcs-gutter));
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
}

/* Centred on the shell, whatever the panels do. Only its controls take pointer events. */
.topBar {
  position: absolute;
  top: var(--bcs-gutter);
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
}

/* View control, from the Figma "Will Edits" frame (Bend-Creator 68:10666). */
.viewSegmentRoot {
  height: var(--bcs-top-bar-height);
  padding: 2px;
  background: var(--bcs-gray-1);
  border-radius: 6px;
}

.viewSegmentControl {
  width: 83px;
}

.viewSegmentIndicator {
  background: #fff;
  border: 1px solid var(--bcs-border-control);
  border-radius: 6px;
  box-shadow: none;
}

.viewSegmentLabel {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-family: var(--bcs-sans);
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  color: var(--bcs-t50);

  &:hover,
  &[data-active] {
    color: var(--bcs-t87);
  }
}

/* The 40px Exit button in the top bar. */
.topBarTextButton {
  height: var(--bcs-top-bar-height);
  padding: 0 14px;
  background: #fff;
  border: 1px solid var(--bcs-border-control);
  border-radius: 6px;
  font-family: var(--bcs-sans);
  font-size: 14px;
  font-weight: 500;
  color: var(--bcs-t87);

  &:hover {
    background: var(--bcs-control-hover);
  }
}
